import type { ScheduledMode } from './multimodal/data.ts'
import {
  formatJourneyMinutes,
  journeyWalkingTime,
  modeRouteDetails,
  modeRouteSummary,
  selectedAlternative,
  type EndpointJourney,
  type ModeRouteStep,
  type RoadMode,
  type RoutedParticipant,
  type RoutedPerson,
  type TravelMode,
} from './transit/modes.ts'

type ModeIcon = ScheduledMode | RoadMode | 'bike-share' | 'walk'

export function routeSummaryHtml(
  label: string,
  routed: RoutedPerson | RoutedParticipant,
  journey: EndpointJourney,
  presentation: 'compact' | 'detailed' = 'detailed',
): string {
  const person = 'alternatives' in routed ? selectedAlternative(routed, journey) : routed
  const summary = routeSummaryText(label, routed, journey)
  if (presentation === 'compact') return compactRouteSummaryHtml(label, summary, person, journey)
  if (journey.kind === 'walk') {
    return `
      <div class="route-line">
        <span class="visually-hidden">${escapeHtml(summary)}</span>
        <b aria-hidden="true">${escapeHtml(label)}:</b>
        <span class="route-steps" aria-hidden="true">
          <span class="route-step route-walk">${modeIcon('walk')}<span>${journey.source === 'routed' ? 'Walk' : 'Walk estimate'}</span></span>
        </span>
      </div>
    `
  }
  if ('kind' in journey && journey.kind === 'bike-share') {
    const access = escapeHtml(formatJourneyMinutes(journey.accessSeconds))
    const ride = escapeHtml(formatJourneyMinutes(journey.rideSeconds))
    const egress = escapeHtml(formatJourneyMinutes(journey.egressSeconds))
    return `
      <div class="route-line">
        <span class="visually-hidden">${escapeHtml(summary)}</span>
        <b aria-hidden="true">${escapeHtml(label)}:</b>
        <span class="route-steps" aria-hidden="true">
          <span class="route-step route-walk">${modeIcon('walk')}<span>${access} to ${escapeHtml(journey.pickupStation.name)}</span></span>
          <span class="route-arrow" aria-hidden="true">→</span>
          <span class="route-step route-ride">${modeIcon('bike-share')}<span>${ride} to ${escapeHtml(journey.returnStation.name)}</span></span>
          <span class="route-arrow" aria-hidden="true">→</span>
          <span class="route-step route-walk">${modeIcon('walk')}<span>${egress} walk</span></span>
        </span>
      </div>
    `
  }
  if (!('journey' in journey)) {
    if (!('mode' in journey)) throw new Error('Unknown route-summary journey type.')
    return `
      <div class="route-line">
        <span class="visually-hidden">${escapeHtml(summary)}</span>
        <b aria-hidden="true">${escapeHtml(label)}:</b>
        <span class="route-steps" aria-hidden="true">
          <span class="route-step">${modeIcon(journey.mode)}<span>${escapeHtml(modeLabel(journey.mode))} estimate</span></span>
        </span>
      </div>
    `
  }
  if ('origin' in person) throw new Error('Walking alternative has another journey type.')
  const details = modeRouteDetails(person, journey)
  const endpoint = `<span class="route-endpoint">to ${escapeHtml(details.endpoint)}</span>`
  const steps = details.steps.length
    ? details.steps.map((step, index) => {
      if (step.kind === 'ride') return routeLabelHtml(
        step,
        step.origin || step.destination
          ? `<span class="route-endpoint">${step.origin
            ? `${escapeHtml(step.origin)}${step.destination ? ` → ${escapeHtml(step.destination)}` : ''}`
            : `to ${escapeHtml(step.destination)}`}</span>`
          : index === details.steps.length - 1 ? endpoint : '',
      )
      const duration = escapeHtml(formatJourneyMinutes(step.seconds))
      const next = details.steps[index + 1]
      const redundantWalkDestination = step.kind === 'walk' && (
        (next?.kind === 'ride' && next.origin !== '' && next.origin === step.destination)
        || (index === details.steps.length - 1 && step.destination === 'destination')
      )
      const access = step.kind === 'bike'
        ? `${modeIcon('bike')}<span>${duration} and park${step.destination ? ` at ${escapeHtml(step.destination)}` : ''}</span>`
        : step.kind === 'bike-share'
          ? `${modeIcon('bike-share')}<span>${duration}${step.destination ? ` to ${escapeHtml(step.destination)}` : ''}</span>`
          : step.kind === 'transfer'
            ? `<span>Transfer ${duration}${step.destination ? ` to ${escapeHtml(step.destination)}` : ''}</span>`
            : `${modeIcon('walk')}<span>Walk ${duration}${step.destination && !redundantWalkDestination ? ` to ${escapeHtml(step.destination)}` : ''}</span>`
      return `
        <span class="route-step route-${step.kind}">
          ${access}
        </span>
      `
    }).join('<span class="route-arrow" aria-hidden="true">→</span>')
    : `<span class="route-step route-walk">${modeIcon('walk')}<span>Walk</span>${endpoint}</span>`
  return `
    <div class="route-line">
      <span class="visually-hidden">${escapeHtml(summary)}</span>
      <b aria-hidden="true">${escapeHtml(label)}:</b>
      <span class="route-steps" aria-hidden="true">${steps}</span>
    </div>
  `
}

export function walkingSummaryHtml(journey: EndpointJourney): string {
  const walking = journeyWalkingTime(journey)
  if (!walking) return ''
  return `<span class="participant-walking">Includes ${escapeHtml(formatJourneyMinutes(walking.seconds))} walking${
    walking.complete ? '' : '<span class="walking-caveat">Station-transfer walking not included</span>'
  }</span>`
}

function compactRouteSummaryHtml(
  label: string,
  summary: string,
  person: ReturnType<typeof selectedAlternative> | RoutedPerson,
  journey: EndpointJourney,
): string {
  let steps: string
  if (journey.kind === 'walk') {
    steps = `<span class="route-step route-walk">${modeIcon('walk')}<span>Walk</span></span>`
  } else if (journey.kind === 'bike-share') {
    steps = `<span class="route-step route-bike-share">${modeIcon('bike-share')}<span>Bike share</span></span>`
  } else if (journey.kind === 'road') {
    steps = `<span class="route-step">${modeIcon(journey.mode)}<span>${escapeHtml(modeLabel(journey.mode))}</span></span>`
  } else {
    if ('origin' in person) throw new Error('Walking alternative has another journey type.')
    const rides = modeRouteDetails(person, journey).steps.filter((step) => step.kind === 'ride')
    steps = rides.length
      ? rides.map((step) => routeLabelHtml(step, '')).join('<span class="route-arrow" aria-hidden="true">→</span>')
      : `<span class="route-step route-walk">${modeIcon('walk')}<span>Walk</span></span>`
  }
  return `
    <div class="route-line">
      <span class="visually-hidden">${escapeHtml(summary)}</span>
      <b aria-hidden="true">${escapeHtml(label)}:</b>
      <span class="route-steps" aria-hidden="true">${steps}</span>
    </div>
  `
}

function routeSummaryText(
  label: string,
  routed: RoutedPerson | RoutedParticipant,
  journey: EndpointJourney,
): string {
  const person = 'alternatives' in routed ? selectedAlternative(routed, journey) : routed
  return `${label}: ${modeRouteSummary(person, journey)}`
}

export function modeIcon(mode: ModeIcon): string {
  const paths = {
    subway: '<rect x="5" y="3" width="14" height="17" rx="4"/><path d="M8 7h8M8 14h.01M16 14h.01M8 20l-2 3m10-3 2 3"/>',
    bus: '<path d="M5 21V6c0-2 2-3 7-3s7 1 7 3v15"/><path d="M5 11h14M8 16h.01M16 16h.01M7 21v2m10-2v2"/>',
    ferry: '<path d="M3 16h18l-3 5H6l-3-5Z"/><path d="M8 16V9h8v7M10 9V6h4v3M4 23c2 1 4 1 6 0s4-1 6 0 4 1 6 0"/>',
    taxi: '<path d="M5 17h14l-1-7-2-3H8l-2 3-1 7Z"/><path d="M7 10h10M7 17v3m10-3v3M8 14h.01M16 14h.01M10 7V4h4v3"/>',
    bike: '<circle cx="6" cy="17" r="4"/><circle cx="18" cy="17" r="4"/><path d="m6 17 4-8 4 8m-6-4h8l-2-5h3M9 6h3"/>',
    'bike-share': '<circle cx="6" cy="17" r="4"/><circle cx="18" cy="17" r="4"/><path d="m6 17 4-8 4 8m-6-4h8l-2-5h3M9 6h3"/>',
    walk: '<path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7Z"/>',
  }
  return `<svg class="mode-icon mode-icon-${mode}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths[mode]}</svg>`
}

export function modeLabel(mode: TravelMode): string {
  if (mode === 'taxi') return 'Taxi / ride-share'
  if (mode === 'bike') return 'Bicycle'
  if (mode === 'bike-share') return 'Bike share'
  return `${mode.charAt(0).toUpperCase()}${mode.slice(1)}`
}

function routeLabelHtml(step: Extract<ModeRouteStep, { kind: 'ride' }>, endpoint: string): string {
  const service = step.mode === 'subway' ? 'train' : step.mode
  return `
    <span class="route-step route-ride">
      ${modeIcon(step.mode)}
      <span class="route-label">${escapeHtml(step.label)} ${service}</span>
      ${endpoint}
    </span>
  `
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character]!)
}
